import React, { useState } from 'react';
import { ChevronDown, Bell, User } from 'lucide-react';

interface TopBarProps {
  currentGym: {
    name: string;
    logoUrl?: string;
  };
  gyms: Array<{
    id: string;
    name: string;
    logoUrl?: string;
  }>;
  onGymSwitch: (gymId: string) => void;
  onCreateGym: () => void;
}

export default function TopBar({ currentGym, gyms, onGymSwitch, onCreateGym }: TopBarProps) {
  const [gymDropdownOpen, setGymDropdownOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);

  return (
    <div className="fixed top-0 left-0 right-0 h-16 bg-[#1a1818] border-b border-white/10 z-50">
      <div className="h-full flex items-center justify-between">
        {/* Left Section - RollOps Logo + Workspace Switcher */}
        <div className="flex items-center min-w-0">
          {/* RollOps Logo - Centered in collapsed sidebar width on desktop, smaller on mobile */}
          <div className="w-12 md:w-20 flex items-center justify-center shrink-0">
            <img 
              src="/rollops-logo.png" 
              alt="RollOps" 
              className="w-8 h-8 object-contain"
            />
          </div>

          {/* Vertical Divider - Hidden on mobile */}
          <div className="hidden md:block h-8 w-px bg-white/20" />

          {/* Workspace Switcher */}
          <div className="relative ml-2 md:ml-4 min-w-0">
          <button
            onClick={() => setGymDropdownOpen(!gymDropdownOpen)}
            className="flex items-center gap-2 md:gap-3 px-2 md:px-3 py-2 rounded-lg hover:bg-white/5 transition-colors min-w-0"
          >
            {/* Gym Logo */}
            <div className="w-8 h-8 rounded-full bg-rollops-primary flex items-center justify-center text-text-primary font-bold text-sm shrink-0">
              {currentGym.logoUrl ? (
                <img src={currentGym.logoUrl} alt={currentGym.name} className="w-full h-full rounded-full object-cover" />
              ) : (
                currentGym.name.charAt(0).toUpperCase()
              )}
            </div>
            
            {/* Gym Name - Truncates on small screens */}
            <span className="text-text-primary font-medium text-sm md:text-base truncate max-w-[120px] sm:max-w-[200px] md:max-w-none">
              {currentGym.name}
            </span>
            
            {/* Chevron */}
            <ChevronDown className={`w-4 h-4 text-text-tertiary transition-transform shrink-0 ${gymDropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Gym Switcher Dropdown */}
          {gymDropdownOpen && (
            <>
              <div 
                className="fixed inset-0 z-40" 
                onClick={() => setGymDropdownOpen(false)}
              />
              <div className="absolute top-full left-0 mt-2 w-64 bg-[#221f1f] border border-white/10 rounded-lg shadow-lg z-50 py-2">
                {gyms.map((gym) => (
                  <button
                    key={gym.id}
                    onClick={() => {
                      onGymSwitch(gym.id);
                      setGymDropdownOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors text-left"
                  >
                    <div className="w-8 h-8 rounded-full bg-rollops-primary flex items-center justify-center text-text-primary font-bold text-sm shrink-0">
                      {gym.logoUrl ? (
                        <img src={gym.logoUrl} alt={gym.name} className="w-full h-full rounded-full object-cover" />
                      ) : (
                        gym.name.charAt(0).toUpperCase()
                      )}
                    </div>
                    <span className="text-text-primary font-medium">{gym.name}</span>
                  </button>
                ))}
                
                {/* Create New Gym */}
                <div className="border-t border-white/10 mt-2 pt-2">
                  <button
                    onClick={() => {
                      onCreateGym();
                      setGymDropdownOpen(false);
                    }}
                    className="w-full px-4 py-3 hover:bg-white/5 transition-colors text-left text-rollops-primary font-medium"
                  >
                    + Create New Gym
                  </button>
                </div>
              </div>
            </>
          )}
          </div>
        </div>

        {/* Middle Section - Reserved for Global Search (hidden on mobile) */}
        <div className="hidden lg:flex flex-1 max-w-2xl mx-8">
          <div className="h-10 w-full rounded-lg border border-white/10 bg-white/5 flex items-center px-4">
            <span className="text-text-muted text-sm italic">Global search (coming soon)</span>
          </div>
        </div>

        {/* Spacer for mobile when search is hidden */}
        <div className="flex-1 lg:hidden" />

        {/* Right Section - Utilities */}
        <div className="flex items-center gap-2 md:gap-4 pr-3 md:pr-4">
          {/* Notification Bell */}
          <button className="relative p-2 rounded-lg hover:bg-white/5 transition-colors">
            <Bell className="w-5 h-5 text-text-secondary" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-rollops-primary rounded-full" />
          </button>

          {/* User Avatar Dropdown */}
          <div className="relative">
            <button
              onClick={() => setUserDropdownOpen(!userDropdownOpen)}
              className="p-1 rounded-full hover:ring-2 hover:ring-white/20 transition-all"
            >
              <div className="w-9 h-9 rounded-full bg-[#2a2727] flex items-center justify-center">
                <User className="w-5 h-5 text-text-secondary" />
              </div>
            </button>

            {/* User Menu Dropdown */}
            {userDropdownOpen && (
              <>
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setUserDropdownOpen(false)}
                />
                <div className="absolute top-full right-0 mt-2 w-56 bg-[#221f1f] border border-white/10 rounded-lg shadow-lg z-50 py-2">
                  <button className="w-full px-4 py-3 hover:bg-white/5 transition-colors text-left text-text-primary">
                    Account Settings
                  </button>
                  <button className="w-full px-4 py-3 hover:bg-white/5 transition-colors text-left text-text-primary">
                    Help & Support
                  </button>
                  <div className="border-t border-white/10 mt-2 pt-2">
                    <button className="w-full px-4 py-3 hover:bg-white/5 transition-colors text-left text-rollops-primary font-medium">
                      Sign Out
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
